import { useData } from "../hooks/useData";
import Http from "../fetch";
import { useState } from "react";
import { ID } from "../models";
import { downloadFile, equalInLC } from "../helpers";

const TOP_COMPETITORS_PRESET_NAME = 'Top Competitors';

interface iInteractiveList {
    childReports: iInteractiveList[];
    id: number;
    name: string;
    order: number;
    restaurnatListId: number;
}

interface iPresetChain {
    fireflyChainId: number;
    fireflyChainName: string;
}

export interface iPreset {
    chains: iPresetChain[];
    chainSegmentGroupId: number;
    companyId: number;
    cusineId: number;
    id: number;
    isCustom: boolean;
    name: string;
    segmentId: number;
}

interface iPresetGroup {
    preset: iPreset[];
    presetClass: "lsrPresets" | "fsrPresets" | "lsrfsrPresets" | "customPresets";
    presetName: string;
}

interface iSavedQuery {
    query: {
        companyId: number;
        created: string;
        creatorId: string;
        creatorName: string;
        description: string;
        id: number;
        isDeleted: boolean;
        isPublic: boolean;
        title: string;
        userId: string;
    };
    words: string[];
}

interface iGapReportData {
    interactiveLists: iInteractiveList[];
    presets: iPresetGroup[];
    savedQueries: iSavedQuery[];
}

async function getGapReportData(): Promise<iGapReportData> {
    return await Http.get('get menu gaps');
}

export async function downloadTopCompetitorsSimpleReport(chainId: number): Promise<void> {
    const popupData: iGapReportData = await Http.get('get menu gaps');
    const customGroup = popupData.presets?.find((g) => g.presetClass === 'customPresets');
    const preset = customGroup?.preset.find((p) => equalInLC(p.name, TOP_COMPETITORS_PRESET_NAME));
    const restaurants = preset?.chains.map((c) => c.fireflyChainId) ?? [];

    const result: iPrepareReportData = await Http.post('chain get simple gap report', {}, {
        headers: { 'content-type': 'application/json; charset=utf-8' },
        body: JSON.stringify({
            Restaurants: restaurants,
            PresetName: TOP_COMPETITORS_PRESET_NAME,
            chainId,
            entityId: 0,
            reportType: 0,
        }),
    });

    if (result.isSuccess && result.downloadLink) {
        downloadFile(`${process.env.REACT_APP_API}${result.downloadLink}`);
        return;
    }
    throw new Error('Failed to prepare simple gap report');
}

interface iPrepareReportData {
    downloadLink: string;
    isSuccess: boolean;
}

interface iReportRequestData {
    Restaurants: ID[];
    PresetName: string;
    chainId: ID;
    entityId: ID;
    reportType: 0 | 1;
}

export function useGapReportData(chainId: number) {
    const { isLoading, ...data } = useData<iGapReportData>({
        interactiveLists: [],
        presets: [],
        savedQueries: [],
    }, getGapReportData);
    const [isReportLoading, setIsReportLoading] = useState(false);

    const handleReportDownload = (action: string, data: iReportRequestData): Promise<boolean> => {
        return new Promise((res, rej) => {
            Http.post(action, {}, {
                headers: {
                    'content-type': 'application/json; charset=utf-8',
                },
                body: JSON.stringify(data)
            }).then((response: iPrepareReportData) => {
                if (response.isSuccess && response.downloadLink) {
                    downloadFile(`${process.env.REACT_APP_API}${response.downloadLink}`);
                    res(true);
                } else {
                    rej(false);
                }
            }).catch(() => {
                rej(false);
            }).finally(() => {
                setIsReportLoading(false);
            });
        });
    };

    const downloadRegularReport = (chainsIds: ID[], presetName: string, reportId: ID, isSavedQuery?: boolean): Promise<boolean> => {
        setIsReportLoading(true);
        return handleReportDownload('chain get gap report', {
            Restaurants: chainsIds,
            PresetName: presetName,
            chainId,
            entityId: reportId,
            reportType: isSavedQuery ? 1 : 0,
        });
    };

    const downloadSimpleReport = (chainsIds: ID[], presetName: string) => {
        setIsReportLoading(true);
        return handleReportDownload('chain get simple gap report', {
            Restaurants: chainsIds,
            PresetName: presetName,
            chainId,
            entityId: 0,
            reportType: 0,
        });
    };

    return {
        isLoading,
        isReportLoading,
        ...data,
        downloadReport: downloadRegularReport,
        downloadSimpleReport,
    };
}